import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components';

export const PrivacyPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | PrintDeck Direct';
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center overflow-x-hidden">
      <div className="w-full max-w-4xl space-y-6">
        {/* Navigation back */}
        <div className="flex items-center justify-between">
          <Link to="/login" className="inline-flex items-center text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Sign In
          </Link>
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="PrintDeck Logo" className="w-6 h-6 object-contain" width={24} height={24} />
            <span className="text-xs font-bold text-slate-900 tracking-tight">PrintDeck Direct</span>
          </div>
        </div>

        {/* Legal Card */}
        <Card className="p-8 sm:p-12 !rounded-[24px] border border-slate-200 bg-white shadow-[0_12px_40px_-12px_rgba(15,23,42,0.08)]">
          <div className="space-y-8">
            {/* Title Header */}
            <div className="border-b border-slate-100 pb-6 text-center sm:text-left">
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Privacy Policy</h1>
              <p className="mt-2 text-xs text-slate-500">Last updated: June 20, 2026</p>
            </div>

            {/* Document Content */}
            <div className="prose prose-slate max-w-none text-slate-600 text-sm leading-relaxed space-y-6">
              
              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">1. Commitment to Data Integrity</h2>
                <p>
                  PrintDeck Direct (the "Service") values your commercial privacy. This Privacy Policy details the metrics we compile, how we process incoming client media, and the strict operational limits we impose to isolate and secure business communication logs.
                </p>
                <p>
                  By deploying our WhatsApp printing bridge, you consent to the data handling protocols described in this document.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">2. Processing vs. Storage: Transient Attachment Spooling</h2>
                <p>
                  Our system is built on a **Zero-Permanence Processing Architecture** for customer attachments. 
                </p>
                <p className="bg-slate-50 border-l-4 border-slate-300 p-3 italic text-slate-500 text-xs">
                  When a customer sends a document to your connected WhatsApp line, PrintDeck Direct acts as a conduit. We fetch the document content temporarily to parse it into print-ready format. Once the document file is delivered to your designated print queue client or local storage spool, it is cleared from our active server memory registers. We do not index, compile database profiles, or hold persistent records of your customers' media attachments.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">3. Types of Information We Collect</h2>
                <p>
                  To manage your account, configuration keys, and session parameters, we collect:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li><strong>Account Metadata:</strong> Email, company identifier, sign-up dates, and active subscription profiles.</li>
                  <li><strong>Integration Tokens:</strong> Hashed connection key strings to link WhatsApp endpoints securely.</li>
                  <li><strong>Usage Logging:</strong> Simple timestamps, order counts, and queue success rates for diagnostic analytics.</li>
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">4. Sub-Processors & Data Architecture</h2>
                <p>
                  We coordinate with select infrastructure providers to run our cloud databases and authorization logic:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li><strong>Supabase:</strong> Serves as our primary client-side user database and authentication engine. User credentials and configuration schema variables reside under Supabase's secure data storage pools.</li>
                  <li><strong>Cloudflare / Amazon Web Services:</strong> Standard CDN delivery to securely routing server request payloads.</li>
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">5. Security Standards & Collaborative Integrity</h2>
                <p>
                  We leverage **industry-recognized technical precautions** including TLS/SSL encryption for all data transport streams and strict access tokens for dashboard queries. 
                </p>
                <p>
                  Our security architecture is built on a **collaborative integrity framework**. While we focus on maintaining robust encryption and access tokens throughout our transit layers, the ultimate security of your print output is a shared journey. You are responsible for ensuring that your local printing network, router interfaces, and machine administration are configured in accordance with modern security standards to support overall connection safety.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-base font-bold text-slate-900">6. Data Deletion and Portability</h2>
                <p>
                  You retain ownership of your configuration data. If you delete your PrintDeck account, your credentials, configuration parameters, and access tokens are scrubbed from our systems and Supabase databases.
                </p>
              </section>

            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;
